import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Link,
} from "@mui/material";
import {
  Save as SaveIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Undo as UndoIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";
import FileUploadComponent from "../../../../components/FileUploadComponent";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const MarkLabStatus = () => {
  const [formData, setFormData] = useState({
    userName: "",
    selectedApplication: "",
    currentStage: "",
    newStage: "",
    remark: "",
    extendDate: null,
  });

  const [applications, setApplications] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showApplicationDropdown, setShowApplicationDropdown] = useState(false);
  const [showMainForm, setShowMainForm] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [existingFileUrl, setExistingFileUrl] = useState("");
  const [isUpdate, setIsUpdate] = useState(false);
  const [showRollback, setShowRollback] = useState(false);
  const [showMarkActive, setShowMarkActive] = useState(false);
  const [showExtendDate, setShowExtendDate] = useState(false);
  const [appDetails, setAppDetails] = useState({
    appId: "",
    stage: "",
    description: "",
  });

  // Fetch stages on component mount
  useEffect(() => {
    fetchStages();
  }, []);

  const fetchStages = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/common/get-stages`
      );
      if (res.data.success) {
        setStages(res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching stages:", err);
    }
  };

  const handleSearchUser = async () => {
    if (!formData.userName.trim()) {
      setError("Please enter a username");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/common/applications-by-username/${formData.userName}`
      );
      if (res.data.success && res.data.data && res.data.data.length > 0) {
        setApplications(res.data.data);
        setShowApplicationDropdown(true);
        setShowMainForm(false);
      } else {
        setError("No applications found for this username");
        setApplications([]);
        setShowApplicationDropdown(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error searching applications");
      setApplications([]);
      setShowApplicationDropdown(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApplicationChange = async (e) => {
    const appId = e.target.value;
    setFormData({ ...formData, selectedApplication: appId });

    if (!appId) {
      setShowMainForm(false);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/common/get-mark-lab-status/${appId}`
      );
      
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setAppDetails({
          appId: data.appid,
          stage: data.stage,
          description: data.description,
        });

        if (data.existingStatus) {
          // Update mode
          setIsUpdate(true);
          setShowRollback(true);
          setFormData({
            ...formData,
            selectedApplication: appId,
            currentStage: data.description || "",
            newStage: data.existingStatus.NewStage || "",
            remark: data.existingStatus.Remark || "",
            extendDate: data.existingStatus.extenddate
              ? dayjs(data.existingStatus.extenddate, "DD/MM/YYYY")
              : null,
          });

          if (data.existingStatus.Fileupload) {
            setExistingFileUrl(data.existingStatus.Fileupload);
          }

          // Check if Mark Active should be shown
          if (
            data.existingStatus.NewStage === "13" &&
            data.description === ""
          ) {
            setShowMarkActive(true);
            setShowExtendDate(true);
          }
        } else {
          // New entry mode
          setIsUpdate(false);
          setShowRollback(false);
          setShowMarkActive(false);
          setFormData({
            ...formData,
            selectedApplication: appId,
            currentStage: data.description || "",
            newStage: "",
            remark: "",
            extendDate: null,
          });
          setExistingFileUrl("");
        }

        setShowMainForm(true);
      } else {
        setError("Application details not found");
        setShowMainForm(false);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching application details");
      setShowMainForm(false);
    } finally {
      setLoading(false);
    }
  };

  const handleNewStageChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, newStage: value });

    // Show extend date field if current stage is empty and new stage is 13
    if (formData.currentStage === "" && value === "13") {
      setShowExtendDate(true);
    } else {
      setShowExtendDate(false);
    }
  };

  const handleFileUpload = (fieldName, uploadData) => {
    setUploadedFile(uploadData);
    setExistingFileUrl("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.newStage) {
      setError("Please select a new stage");
      return;
    }

    if (!formData.remark.trim()) {
      setError("Please enter a remark");
      return;
    }

    if (!uploadedFile && !existingFileUrl) {
      setError("Please upload a file");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
      appId: appDetails.appId,
      currentStage: appDetails.stage,
      newStage: formData.newStage,
      remark: formData.remark,
      insertedBy: localStorage.getItem("orgId") || "",
      existingFile: uploadedFile?.fileName || existingFileUrl || "",
      extendDate:
      showExtendDate && formData.extendDate
      ? formData.extendDate.format("DD/MM/YYYY")
      : null,
    };
      let res;  
      if (isUpdate) {
        res = await axios.put(
          `${process.env.REACT_APP_API_URL}/common/update-mark-lab-status`,
          payload
        );
      } else {
        res = await axios.post(
          `${process.env.REACT_APP_API_URL}/common/save-mark-lab-status`,
          payload
        );
      }

      if (res.data.success) {
        setSuccessMessage(isUpdate ? "Data updated successfully" : "Data saved successfully");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error saving data");
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async () => {
    if (!formData.remark.trim()) {
      setError("Please enter a remark for rollback");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/common/rollback-mark-lab-status`,
        {
          appId: appDetails.appId,
          remark: formData.remark,
          insertedBy: localStorage.getItem("orgId") || "",
        }
      );

      if (res.data.success) {
        setSuccessMessage("Rollback performed successfully");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error performing rollback");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkActive = async () => {
    if (!formData.remark.trim()) {
      setError("Please enter a remark");
      return;
    }

    if (!uploadedFile && !existingFileUrl) {
      setError("Please upload a file");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = {
      appId: appDetails.appId,
      currentStage: appDetails.stage,
      newStage: formData.newStage,
      remark: formData.remark,
      insertedBy: localStorage.getItem("orgId") || "",
      existingFile: uploadedFile?.fileName || existingFileUrl || null,
      extendDate: formData.extendDate
      ? formData.extendDate.format("DD/MM/YYYY")
      : null,
  };
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/common/mark-active`,
        payload
      );

      if (res.data.success) {
        setSuccessMessage("Marked as active successfully");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error marking as active");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      userName: "",
      selectedApplication: "",
      currentStage: "",
      newStage: "",
      remark: "",
      extendDate: null,
    });
    setApplications([]);
    setShowApplicationDropdown(false);
    setShowMainForm(false);
    setUploadedFile(null);
    setExistingFileUrl("");
    setIsUpdate(false);
    setShowRollback(false);
    setShowMarkActive(false);
    setShowExtendDate(false);
    setError("");
    setSuccessMessage("");
  };

  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`, 
      originalName: fileName,
    };
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
              <Typography variant="h4" sx={{ flexGrow: 1, fontWeight: "bold" }}>
                Mark Lab Status
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {error}
              </Alert>
            )}

            {successMessage && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage("")}>
                {successMessage}
              </Alert>
            )}

            {/* Search Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={5}>
                  <Typography variant="body1" sx={{ textAlign: "right" }}>
                    User Name <span style={{ color: "red" }}>*</span>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    name="userName"
                    value={formData.userName}
                    onChange={(e) => setFormData({ ...formData, userName: e.target.value })}
                    placeholder="Enter User Name"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearchUser}
                    disabled={loading}
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Application Dropdown Section */}
            {showApplicationDropdown && (
              <>
                <Divider sx={{ my: 2 }} />
                <Paper sx={{ p: 3, mb: 3 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Typography variant="body1" sx={{ textAlign: "right" }}>
                        All Applications <span style={{ color: "red" }}>*</span>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={formData.selectedApplication}
                          onChange={handleApplicationChange}
                          displayEmpty
                        >
                          <MenuItem value="">
                            -----------Select Application----------
                          </MenuItem>
                          {applications.map((app) => (
                            <MenuItem key={app.AppId} value={app.AppId}>
                              {app.Name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </>
            )}

            {/* Main Form Section */}
            {showMainForm && (
              <>
                <Divider sx={{ my: 2 }} />
                <Paper sx={{ p: 3 }}>
                  <form onSubmit={handleSubmit}>
                    {/* Current Stage */}
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12} md={5}>
                        <Typography variant="body1" sx={{ textAlign: "right" }}>
                          Current Stage 
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          value={formData.currentStage}
                          disabled
                          size="small"
                        />
                      </Grid>
                    </Grid>

                    {/* New Stage */}
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12} md={5}>
                        <Typography variant="body1" sx={{ textAlign: "right" }}>
                          New Stage <span style={{ color: "red" }}>*</span>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <Select
                            value={formData.newStage}
                            onChange={handleNewStageChange}
                            displayEmpty
                          >
                            <MenuItem value="">--Select New Stage--</MenuItem>
                            {stages.map((stage) => (
                              <MenuItem key={stage.stage} value={stage.stage}>
                                {stage.Description}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    {/* Upload File */}
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12} md={5}>
                        <Typography variant="body1" sx={{ textAlign: "right" }}>
                          {/* Label is handled by FileUploadComponent */}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={7}>
                        <FileUploadComponent
                          fieldName="markLabStatusFile"
                          label="Upload File"
                          accept=".pdf,.jpg,.jpeg,.png"
                          maxSize={10}
                          onChange={handleFileUpload}
                          initialFile={existingFileUrl ? createFileObject(existingFileUrl) : uploadedFile}
                          localDeleteOnly={true}
                          required={true}
                        />
                      </Grid>
                    </Grid>

                    {/* Remark */}
                    <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={12} md={5}>
                        <Typography variant="body1" sx={{ textAlign: "right" }}>
                          Remark <span style={{ color: "red" }}>*</span>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          name="remark"
                          value={formData.remark}
                          onChange={(e) =>
                            setFormData({ ...formData, remark: e.target.value })
                          }
                          placeholder="Enter Remarks"
                          size="small"
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>

                    {/* Extend Date */}
                    {showExtendDate && (
                      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Grid item xs={12} md={5}>
                          <Typography variant="body1" sx={{ textAlign: "right" }}>
                            Extend Date
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <DatePicker
                            value={formData.extendDate}
                            onChange={(newValue) =>
                              setFormData({ ...formData, extendDate: newValue })
                            }
                            minDate={dayjs()}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: "small",
                                placeholder: "Select Date",
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {/* Action Buttons */}
                    <Grid container spacing={2} sx={{ mt: 3 }}>
                      <Grid item xs={12} sx={{ textAlign: "center" }}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          startIcon={<SaveIcon />}
                          disabled={loading}
                          sx={{ mr: 2 }}
                        >
                          {isUpdate ? "Update" : "Save"}
                        </Button>

                        {showRollback && (
                          <Button
                            variant="contained"
                            color="error"
                            startIcon={<UndoIcon />}
                            onClick={handleRollback}
                            disabled={loading}
                            sx={{ mr: 2 }}
                          >
                            Rollback
                          </Button>
                        )}

                        {showMarkActive && (
                          <Button
                            variant="contained"
                            color="warning"
                            startIcon={<CheckCircleIcon />}
                            onClick={handleMarkActive}
                            disabled={loading}
                            sx={{ mr: 2 }}
                          >
                            Mark Active
                          </Button>
                        )}

                        <Button
                          variant="outlined"
                          startIcon={<RefreshIcon />}
                          onClick={handleReset}
                          disabled={loading}
                        >
                          Reset
                        </Button>
                      </Grid>
                    </Grid>
                  </form>
                </Paper>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default MarkLabStatus;
